"use client";

import { ArrowRight } from "lucide-react";
import { motion } from "motion/react";
import { useRouter } from "next/navigation";

const steps = [
  {
    step: "01",
    title: "Apply",
    description: "Tell us what you cook, how you work, and the kind of food presence you want to build.",
  },
  {
    step: "02",
    title: "Get verified",
    description: "We review creators and kitchens so Noshers can discover standout food with confidence.",
  },
  {
    step: "03",
    title: "Start growing",
    description: "Start posting, grow your audience, and turn on orders when it fits where you are.",
  },
];

export function FoodCreatorProcess() {
  const router = useRouter();

  return (
    <section className="bg-[#fffaf7] py-20 lg:py-24" id="start" data-section-theme="light">
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-10">
        <div className="mb-10 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="max-w-3xl font-asgard text-4xl leading-[0.96] tracking-[-0.04em] text-[#02120A] sm:text-5xl">
              A clear path from first post to first loyal regular
            </h2>
            <motion.p
              className="mt-5 max-w-2xl font-satoshi text-lg leading-8 text-[#02120A]/72"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              You do not have to launch everything at once. Start with your food, earn attention, and build into orders at your own pace.
            </motion.p>
          </motion.div>
        </div>

        <div className="grid gap-8 lg:grid-cols-3 lg:gap-10">
          {steps.map((step, index) => (
            <motion.div
              key={step.step}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="border-t border-[#ff5f47]/18 pt-6">
                <span className="font-satoshi text-sm font-bold tracking-[0.24em] text-[#0A5C36]/60">
                  {step.step}
                </span>
                <h3 className="mt-5 font-asgard text-3xl leading-tight text-[#02120A]">
                  {step.title}
                </h3>
                <p className="mt-3 max-w-[18rem] font-satoshi text-base leading-7 text-[#02120A]/70">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="mt-8 flex flex-col gap-3 sm:flex-row"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45, delay: 0.2 }}
        >
          <button
            type="button"
            onClick={() => router.push("/become-a-food-creator/apply?type=food-creator")}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FF5F47] px-6 py-3.5 font-satoshi text-sm font-bold text-white transition hover:bg-[#ff715c]"
          >
            Apply to cook
            <ArrowRight className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => router.push("/become-a-food-creator/apply?type=studio-owner")}
            className="inline-flex items-center justify-center rounded-full border border-[#0A5C36]/18 bg-white px-6 py-3.5 font-satoshi text-sm font-semibold text-[#02120A] transition hover:border-[#0A5C36]/32"
          >
            List a kitchen instead
          </button>
        </motion.div>
      </div>
    </section>
  );
}
